import { httpsCallable, type HttpsCallable, type HttpsCallableOptions } from 'firebase/functions';
import { functions } from './firebase';
import { DISPATCH_MAP } from './dispatch-map.generated';
import { isInPortal, portalViewGoneOf, withPortalView } from './portal-view';
import { currentPortalView, usePortalViewStore } from '../stores/portal-view.store';

// ─── Llamar a un callable por su nombre de siempre ──────────────────────────
// Las funciones del servidor se están agrupando en un dispatcher por contexto
// (`crmDispatch`, …) que recibe `{ action, payload }`. Para el web nada cambia:
// se sigue llamando por el nombre de siempre, con los mismos tipos de entrada y
// salida, y este helper decide a dónde va.
//
//   · si el nombre está en DISPATCH_MAP (generado desde los registros del
//     servidor), va a su dispatcher como `{ action: nombre, payload }`;
//   · si no, va a la función con ese nombre, como hasta ahora.
//
// Rollback de un contexto: quitarlo de los registros y regenerar el mapa. Sus
// funciones viejas siguen desplegadas hasta el contract.

export function callable<Req = unknown, Res = unknown>(
  name: string,
  options?: HttpsCallableOptions,
): HttpsCallable<Req, Res> {
  const dispatcher = DISPATCH_MAP[name];
  const send = dispatcher
    ? (() => {
        const dispatch = httpsCallable<{ action: string; payload: Req }, Res>(
          functions,
          dispatcher,
          options,
        );
        return (payload: Req) => dispatch({ action: name, payload });
      })()
    : (() => {
        const fn = httpsCallable<Req, Res>(functions, name, options);
        return (payload: Req) => fn(payload);
      })();
  const call = async (data?: Req | null) => {
    try {
      return await send(withViewOf(data as Req));
    } catch (e) {
      noticeGoneView(e);
      throw e;
    }
  };
  return call as HttpsCallable<Req, Res>;
}

// ─── «Ver portal» (lib/portal-view) ─────────────────────────────────────────
// Mientras el personal mira el portal de un alumno, TODA llamada lleva el
// `portalViewId` —también las que no pasan por un dispatcher—: así el servidor
// sabe que es una vista y rechaza cualquier escritura, aunque un botón se
// hubiera quedado sin ocultar. Fuera de las pantallas del portal, nunca.
function withViewOf<T>(payload: T): T {
  const pathname = typeof window === 'undefined' ? '' : window.location.pathname;
  return withPortalView(payload, currentPortalView(), pathname) as T;
}

/** Si el servidor dice que la vista ya no sirve —venció, no vale, o la persona
 *  pasó a figurar como fallecida—, la banda y la página lo dicen. */
function noticeGoneView(e: unknown): void {
  const why = portalViewGoneOf(e);
  const pathname = typeof window === 'undefined' ? '' : window.location.pathname;
  if (why && currentPortalView() && isInPortal(pathname)) {
    usePortalViewStore.getState().markGone(why);
  }
}
